import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Check, TrendingUp, Lock, DollarSign } from 'lucide-react';
import { tracking } from '../../utils/tracking';

export function BestAILifeCoachPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Best AI Life Coach 2025 - VZMO vs TextMei vs Life Coach AI';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Compare the best AI life coach apps of 2025. VZMO offers the best value with career coaching, journaling, and action plans. See how we compare to TextMei and Life Coach AI.'
      );
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://vzmo.ai/compare/best-ai-life-coach');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://vzmo.ai/compare/best-ai-life-coach';
      document.head.appendChild(link);
    }

    window.scrollTo(0, 0);
  }, []);

  const handleStartFree = () => {
    tracking.startFreeClicked('best-ai-coach-comparison');
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-6xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Best AI Life Coach Apps of 2025
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            We compared the top AI life coaching platforms on features, pricing, privacy, and user experience.
            Here's what we found.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-12">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Feature</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-[#E07A5F]">VZMO</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-600">TextMei</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-600">Life Coach AI</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-600">ChatGPT</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr>
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">AI Coaching Conversations</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Structured Journaling</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center text-slate-400">-</td>
                  <td className="px-6 py-4 text-center text-slate-400">-</td>
                  <td className="px-6 py-4 text-center text-slate-400">-</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Action Plans & Milestones</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center text-slate-400">-</td>
                  <td className="px-6 py-4 text-center text-sm text-slate-500">Basic</td>
                  <td className="px-6 py-4 text-center text-slate-400">-</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Career Coaching Tools</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center text-slate-400">-</td>
                  <td className="px-6 py-4 text-center text-slate-400">-</td>
                  <td className="px-6 py-4 text-center text-slate-400">-</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Relationship Coaching</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center text-sm text-slate-500">Generic</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Weekly Progress Reports</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center text-slate-400">-</td>
                  <td className="px-6 py-4 text-center text-slate-400">-</td>
                  <td className="px-6 py-4 text-center text-slate-400">-</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Privacy & Encryption</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center text-sm text-slate-500">Basic</td>
                  <td className="px-6 py-4 text-center text-sm text-slate-500">Limited</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Export Your Data</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center text-sm text-slate-500">Limited</td>
                  <td className="px-6 py-4 text-center text-slate-400">-</td>
                  <td className="px-6 py-4 text-center text-slate-400">-</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Monthly Price</td>
                  <td className="px-6 py-4 text-center text-sm font-semibold text-[#E07A5F]">$7</td>
                  <td className="px-6 py-4 text-center text-sm text-slate-600">$10</td>
                  <td className="px-6 py-4 text-center text-sm text-slate-600">$29</td>
                  <td className="px-6 py-4 text-center text-sm text-slate-600">$20</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Free Trial</td>
                  <td className="px-6 py-4 text-center text-sm font-semibold text-[#E07A5F]">7 days</td>
                  <td className="px-6 py-4 text-center text-sm text-slate-600">3 days</td>
                  <td className="px-6 py-4 text-center text-sm text-slate-600">None</td>
                  <td className="px-6 py-4 text-center text-sm text-slate-600">Limited Free</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Overall Rating</td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-[#E07A5F] text-[#E07A5F]" />
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      {[...Array(4)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-slate-400 text-slate-400" />
                      ))}
                      <Star className="w-4 h-4 text-slate-300" />
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      {[...Array(3)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-slate-400 text-slate-400" />
                      ))}
                      {[...Array(2)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-slate-300" />
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      {[...Array(3)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-slate-400 text-slate-400" />
                      ))}
                      {[...Array(2)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-slate-300" />
                      ))}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#E07A5F] to-[#F4A261] rounded-2xl p-8 sm:p-12 text-white text-center mb-12">
          <Star className="w-16 h-16 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Why VZMO is the Best Choice</h2>
          <p className="text-lg mb-6 opacity-90 max-w-2xl mx-auto">
            VZMO combines the conversational AI of TextMei with structured frameworks, career tools,
            and progress tracking - all at the lowest price point.
          </p>
          <button
            onClick={handleStartFree}
            className="bg-white text-[#E07A5F] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-slate-50 transition-colors"
          >
            Start Free Trial
          </button>
          <p className="mt-4 text-sm opacity-75">7 days free, no credit card required</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <DollarSign className="w-12 h-12 text-[#E07A5F] mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-3">Best Value</h3>
            <p className="text-slate-600 mb-4">
              At $7/month, VZMO offers more features than apps costing 2-4x as much.
            </p>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                30% cheaper than TextMei
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                75% cheaper than Life Coach AI
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                More features than all competitors
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg">
            <TrendingUp className="w-12 h-12 text-[#E07A5F] mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-3">Most Complete Platform</h3>
            <p className="text-slate-600 mb-4">
              VZMO is the only platform with all these features in one place.
            </p>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                AI coaching + journaling
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                Career & relationship tools
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                Progress tracking & reports
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg">
            <Lock className="w-12 h-12 text-[#E07A5F] mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-3">Best Privacy</h3>
            <p className="text-slate-600 mb-4">
              Your data is encrypted, never sold, and fully exportable.
            </p>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                End-to-end encryption
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                Anonymous usage option
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                Export and delete anytime
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">How We Evaluated AI Life Coaches</h2>
          <p className="text-slate-600 mb-6">
            We tested each platform for 30 days, evaluating them on key criteria that matter most to users:
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Coaching Quality</h3>
              <p className="text-sm text-slate-600">
                How helpful and insightful are the AI responses? Do they provide actionable guidance or just generic advice?
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Feature Completeness</h3>
              <p className="text-sm text-slate-600">
                Beyond basic chat, what tools are included? Journaling, action plans, progress tracking, and specialized coaching.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Pricing & Value</h3>
              <p className="text-sm text-slate-600">
                Is the pricing transparent and fair? What's the cost relative to features provided?
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Privacy & Security</h3>
              <p className="text-sm text-slate-600">
                How is user data handled? Is it encrypted, anonymous, and exportable?
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-2">User Experience</h3>
              <p className="text-sm text-slate-600">
                Is the platform easy to use? Does it feel polished and professional?
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Trial & Accessibility</h3>
              <p className="text-sm text-slate-600">
                Can users try it before buying? Is there a free tier or generous trial period?
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
